import { authStore } from '../auth/authStore';
import { readAuthStorage } from '../auth/authStorage';
import {
  deriveAdminFlag,
  getOrCreateDeviceId,
  isJwtExpired,
  isAppEnvironment,
  parseJwt,
} from '../auth/authShared';

export { isAppEnvironment, getOrCreateDeviceId };

export const isTokenExpired = (token) => {
  return isJwtExpired(token);
};

export const getToken = () => {
  const snapshot = authStore.getSnapshot();
  if (snapshot.status === 'unauthenticated') {
    return null;
  }
  return snapshot.accessToken || readAuthStorage().accessToken || null;
};

export const getUserNoFromToken = () => {
  const payload = parseJwt(getToken() || '');
  if (!payload?.sub) {
    return null;
  }

  return String(payload.sub).trim() || null;
};

export const getRefreshToken = () => {
  const snapshot = authStore.getSnapshot();
  if (snapshot.status === 'unauthenticated') {
    return null;
  }
  return snapshot.refreshToken || readAuthStorage().refreshToken || null;
};

export const setAuth = (payload = {}) => {
  return authStore.loginSuccess(payload, {
    source: isAppEnvironment() ? 'app' : 'web',
    preserveExistingIntent: true,
  });
};

export const clearAuth = () => {
  return authStore.logout({ reason: 'user' });
};

export const verifySession = async () => {
  return authStore.verifySession();
};

export const authHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const isAuthenticated = () => {
  const snapshot = authStore.getSnapshot();
  if (snapshot.status === 'authenticated') {
    return true;
  }

  if (snapshot.status === 'unauthenticated') {
    return false;
  }

  return Boolean(getToken());
};

export const isAdmin = () => {
  const snapshot = authStore.getSnapshot();
  if (snapshot.status === 'authenticated') {
    return Boolean(snapshot.isAdmin);
  }

  const token = getToken();
  if (token && deriveAdminFlag(parseJwt(token) || {})) {
    return true;
  }

  return Boolean(readAuthStorage().isAdmin);
};

export const requestTokenFromApp = () => {
  return new Promise((resolve) => {
    let timeoutId = null;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      window.removeEventListener('message', handleTokenResponse);
    };

    const handleTokenResponse = (event) => {
      try {
        const message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (message?.type !== 'APP_TOKEN_RESPONSE') {
          return;
        }

        cleanup();

        if (message.success && message.token) {
          authStore.loginSuccess(
            { access_token: message.token },
            { source: 'app', preserveExistingIntent: true },
          );
          resolve(message.token);
          return;
        }

        resolve(null);
      } catch {
        // ignore unrelated message events
      }
    };

    timeoutId = setTimeout(() => {
      cleanup();
      resolve(null);
    }, 5000);

    window.addEventListener('message', handleTokenResponse);

    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'REQUEST_TOKEN_REFRESH' }));
      return;
    }

    if (window.webkit?.messageHandlers?.appBridge) {
      window.webkit.messageHandlers.appBridge.postMessage({ type: 'REQUEST_TOKEN_REFRESH' });
      return;
    }

    if (window.Android?.requestTokenRefresh) {
      window.Android.requestTokenRefresh();
      return;
    }

    cleanup();
    resolve(null);
  });
};

export const refreshAccessToken = async () => {
  return authStore.refreshSession();
};
